import { useState, type ReactNode } from 'react';
import { App, Flex, Table, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { downloadBlob } from '@/shared/lib/downloadBlob';
import { translateApiError } from '@/shared/api/errorToMessage';
import { useHotkeyActions, useHotkeyScope } from '@/shared/lib/hotkeys';
import { HotkeyButton } from '@/shared/ui/hotkey';
import { useFocusTarget } from '@/shared/ui/inputs';

export interface ReportPageProps<T> {
  title: string;
  filters?: ReactNode;
  columns: ColumnsType<T>;
  data: T[];
  loading?: boolean;
  rowKey: string | ((record: T) => string);
  expandable?: TableProps<T>['expandable'];
  /** Доп. блок над таблицей (например, итоговая строка «Итого: N») */
  summary?: ReactNode;
  onExport: () => Promise<Blob>;
  exportFilename: string;
}

/** §13: общая обёртка для всех 9 отчётов — FilterBar → Table → Печать/Экспорт (§13). */
export function ReportPage<T>({ title, filters, columns, data, loading, rowKey, expandable, summary, onExport, exportFilename }: ReportPageProps<T>) {
  const { message } = App.useApp();
  const [exporting, setExporting] = useState(false);
  const { attach: attachFilters, focus: focusFilters } = useFocusTarget<HTMLDivElement>();

  async function handleExport() {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await onExport();
      downloadBlob(blob, exportFilename);
    } catch (e) {
      message.error(translateApiError(e));
    } finally {
      setExporting(false);
    }
  }

  // Внутри окна отчёта эти клавиши — дочерний скоуп окна, на странице `/reports/*` — клавиши страницы.
  const actions = useHotkeyActions({
    'report.print': { run: () => window.print() },
    'report.export': { run: () => void handleExport(), disabled: exporting ? 'Выгрузка уже выполняется' : false },
    ...(filters ? { 'report.filters': { hint: false, run: focusFilters } } : {}),
  });
  useHotkeyScope(actions.bindings);

  return (
    <Flex vertical gap={12}>
      <Flex justify="space-between" align="center" wrap className="no-print">
        <Typography.Title level={5} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
        <Flex gap={8}>
          <HotkeyButton action={actions.get('report.print')}>Печать</HotkeyButton>
          <HotkeyButton loading={exporting} action={actions.get('report.export')}>
            Экспорт
          </HotkeyButton>
        </Flex>
      </Flex>

      {filters ? (
        <div className="filter-bar no-print" style={{ overflow: 'visible' }} ref={attachFilters}>
          {filters}
        </div>
      ) : null}

      {summary}

      <Table<T>
        size="small"
        rowKey={rowKey}
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={false}
        bordered
        expandable={expandable}
      />
    </Flex>
  );
}
